import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { config } from "../../../config";
import {
  BookOpen,
  FileText,
  Hash,
  Star,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Edit3,
  Save,
  ArrowLeft,
  Book,
  FolderOpen,
  Sigma,
  Divide,
  Square,
  Superscript,
  Subscript
} from "lucide-react";

const apiUrl = config.apiUrl;

const UpdateQuestion = ({ questionId, formData, setFormData, onSuccess, onCancel }) => {
  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const quillRef = useRef(null);

  // React Quill modules configuration
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false,
    }
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  // Insert math symbol at cursor position
  const insertMathSymbol = (symbol) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, symbol);
        quill.setSelection(range.index + symbol.length);
      } else {
        quill.insertText(quill.getLength(), symbol);
      }
    }
  };

  // Custom toolbar component for math symbols
  const MathToolbar = () => (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 mb-4">
      <span className="font-medium text-sm text-gray-700 mr-2">গাণিতিক চিহ্ন:</span>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ভগ্নাংশ যোগ করুন"
        onClick={() => insertMathSymbol('\\frac{a}{b}')}
      >
        <Divide className="h-3 w-3 mr-1" />
        ভগ্নাংশ
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="গুণ করুন"
        onClick={() => insertMathSymbol('\\times')}
      >
        <span className="text-sm font-bold mr-1">×</span>
        গুণ
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="বর্গমূল যোগ করুন"
        onClick={() => insertMathSymbol('\\sqrt{x}')}
      >
        <Square className="h-3 w-3 mr-1" />
        বর্গমূল
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সুপারスクリプ্ট যোগ করুন"
        onClick={() => insertMathSymbol('x^{2}')}
      >
        <Superscript className="h-3 w-3 mr-1" />
        সুপারスクリプ্ট
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সাবスクリプ্ট যোগ করুন"
        onClick={() => insertMathSymbol('x_{2}')}
      >
        <Subscript className="h-3 w-3 mr-1" />
        সাবスクリプ্ট
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সমষ্টি যোগ করুন"
        onClick={() => insertMathSymbol('\\sum_{i=1}^{n}')}
      >
        <Sigma className="h-3 w-3 mr-1" />
        সমষ্টি
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ইন্টিগ্রাল যোগ করুন"
        onClick={() => insertMathSymbol('\\int_{a}^{b} f(x) dx')}
      >
        <span className="text-xs font-bold mr-1">∫</span>
        ইন্টিগ্রাল
      </button>
    </div>
  );

  const difficultyOptions = [
    { value: "easy", label: "Easy", color: "text-green-600" },
    { value: "medium", label: "Medium", color: "text-yellow-600" },
    { value: "hard", label: "Hard", color: "text-red-600" },
  ];

  // Fetch subjects
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiUrl}/subjects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubjects(response.data.data || response.data);
      } catch (err) {
        console.error("Error fetching subjects:", err);
      }
    };

    fetchSubjects();
  }, []);

  useEffect(() => {
    if (formData.subject_id) {
      fetchModulesBySubject(formData.subject_id);
    }
  }, [formData.subject_id]);

  useEffect(() => {
    if (formData.module_id) {
      fetchLessonsByModule(formData.module_id);
    }
  }, [formData.module_id]);

  const fetchModulesBySubject = async (subjectId) => {
    setLoadingModules(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiUrl}/subjects/${subjectId}/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModules(response.data.data || response.data);
    } catch (err) {
      console.error("Error fetching modules:", err);
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchLessonsByModule = async (moduleId) => {
    setLoadingLessons(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiUrl}/modules/${moduleId}/lessons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLessons(response.data.data || response.data);
    } catch (err) {
      console.error("Error fetching lessons:", err);
    } finally {
      setLoadingLessons(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      question_text: value,
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index].option_text = value;
    setFormData((prev) => ({
      ...prev,
      options: newOptions,
    }));
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { option_text: "" }],
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) return;
    
    const newOptions = [...formData.options];
    newOptions.splice(index, 1);
    
    let newCorrectIndex = formData.correct_option_index;
    if (index === formData.correct_option_index) {
      newCorrectIndex = 0;
    } else if (index < formData.correct_option_index) {
      newCorrectIndex = formData.correct_option_index - 1;
    }
    
    setFormData((prev) => ({
      ...prev,
      options: newOptions,
      correct_option_index: newCorrectIndex,
    }));
  };

  const setCorrectAnswer = (index) => {
    setFormData((prev) => ({
      ...prev,
      correct_option_index: index,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${apiUrl}/questionmanagementupdate/${questionId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setSuccess("প্রশ্ন সফলভাবে আপডেট হয়েছে!");
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      console.error("Error updating question:", err);
      setError(err.response?.data?.message || "প্রশ্ন আপডেট করতে সমস্যা হয়েছে");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <button
                onClick={onCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                  <Edit3 className="h-6 w-6 mr-2 text-blue-600" />
                  প্রশ্ন আপডেট করুন
                </h1>
                <p className="text-gray-600 text-sm">প্রশ্নের তথ্য সম্পাদনা করুন</p>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <XCircle className="h-5 w-5 text-red-600 mr-3" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
              <span className="text-green-800">{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subject, Module, and Lesson Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Subject Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                  বিষয় *
                </label>
                <select
                  name="subject_id"
                  value={formData.subject_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">বিষয় নির্বাচন করুন</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Module Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FolderOpen className="h-4 w-4 mr-2 text-blue-600" />
                  মডিউল
                </label>
                <select
                  name="module_id"
                  value={formData.module_id}
                  onChange={handleChange}
                  disabled={!formData.subject_id || loadingModules}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">মডিউল নির্বাচন করুন</option>
                  {loadingModules ? (
                    <option value="">লোড হচ্ছে...</option>
                  ) : (
                    modules.map((module) => (
                      <option key={module.id} value={module.id}>
                        {module.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Lesson Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Book className="h-4 w-4 mr-2 text-blue-600" />
                  লেসন
                </label>
                <select
                  name="lesson_id"
                  value={formData.lesson_id}
                  onChange={handleChange}
                  disabled={!formData.module_id || loadingLessons}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">লেসন নির্বাচন করুন</option>
                  {loadingLessons ? (
                    <option value="">লোড হচ্ছে...</option>
                  ) : (
                    lessons.map((lesson) => (
                      <option key={lesson.id} value={lesson.id}>
                        {lesson.title}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* Mark and Difficulty */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Hash className="h-4 w-4 mr-2 text-blue-600" />
                  মান *
                </label>
                <input
                  type="number"
                  name="mark"
                  value={formData.mark}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Star className="h-4 w-4 mr-2 text-blue-600" />
                  difficulty level
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {difficultyOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.difficulty === option.value
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="difficulty"
                        value={option.value}
                        checked={formData.difficulty === option.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <span className={`font-medium ${option.color}`}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                প্রশ্নের বিবরণ *
              </label>
              
              {/* Math Toolbar for Question */}
              <MathToolbar />
              
              <ReactQuill
                ref={quillRef}
                value={formData.question_text}
                onChange={handleQuillChange}
                modules={quillModules}
                formats={quillFormats}
                theme="snow"
                className="h-48 mb-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="আপনার প্রশ্নটি এখানে লিখুন..."
              />
            </div>

            {/* Options */}
            <div>
              <div className="flex items-center justify-between mb-4 mt-24">
                <label className="block text-sm font-medium text-gray-700">
                  অপশনগুলি *
                </label>
                <button
                  type="button"
                  onClick={addOption}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  অপশন যোগ করুন
                </button>
              </div>

              <div className="space-y-3">
                {formData.options.map((option, index) => (
                  <div
                    key={index}
                    className={`flex items-start space-x-3 p-4 border rounded-lg transition-colors ${
                      formData.correct_option_index === index
                        ? "border-green-500 bg-green-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setCorrectAnswer(index)}
                      className={`p-2 rounded-full mt-1 ${
                        formData.correct_option_index === index
                          ? "bg-green-100 text-green-600"
                          : "bg-gray-100 text-gray-400 hover:text-gray-600"
                      }`}
                      title="সঠিক উত্তর হিসেবে চিহ্নিত করুন"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </button>

                    <div className="flex-1">
                      <input
                        type="text"
                        value={option.option_text}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {formData.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="p-2 text-red-400 hover:text-red-600 mt-1"
                        title="অপশন মুছুন"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {formData.correct_option_index !== null && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center text-green-800">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">
                      সঠিক উত্তর: Option {formData.correct_option_index + 1}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                বাতিল
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    আপডেট হচ্ছে...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    প্রশ্ন আপডেট করুন
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateQuestion;